import { Activity, useMemo, useState } from 'react'
import {
  Box,
  Button,
  Card,
  CardContent,
  FormControl,
  FormControlLabel,
  FormGroup,
  FormHelperText,
  FormLabel,
  InputLabel,
  MenuItem,
  Radio,
  RadioGroup,
  Select,
  Stack,
  Switch,
  TextField,
  Typography,
} from '@mui/material'

import { useColorScheme } from '@mui/material/styles'

// Get all available timezones
const ALL_TIMEZONES = Intl.supportedValuesOf('timeZone').map((zone) => ({
  value: zone,
  label: zone.replace(/_/g, ' ').replace(/\//g, ' / '),
}))

const DATE_FORMAT_OPTIONS = [
  { value: 'iso', label: 'ISO (YYYY-MM-DD)', example: '2024-02-11' },
  { value: 'eur', label: 'European (DD.MM.YYYY)', example: '11.02.2024' },
  { value: 'usa', label: 'US (MM/DD/YYYY)', example: '02/11/2024' },
]

const UserInformationCard = ({
  dateFormat,
  setDateFormat,
  timezone,
  setTimezone,
  setTheme,
  onSaveSettings,
  errors,
}) => {
  const [searchQuery, setSearchQuery] = useState('')

  const { mode, setMode } = useColorScheme()

  const filteredTimezones = useMemo(() => {
    const query = searchQuery.toLowerCase()
    return ALL_TIMEZONES.filter(
      (tz) =>
        tz.label.toLowerCase().includes(query) ||
        tz.value.toLowerCase().includes(query),
    )
  }, [searchQuery])

  const handleDateFormatChange = (event) => {
    setDateFormat(event.target.value)
  }

  const handleTimezoneChange = (event) => {
    setTimezone(event.target.value)
  }

  const handleThemeSwitch = (event) => {
    setMode(event.target.value)
    setTheme(event.target.value)
  }

  const renderTimezonePicker = () => (
    <FormControl error={!!errors.timezone} fullWidth>
      <InputLabel id="timezone-label" htmlFor="timezone">
        Timezone
      </InputLabel>
      <Select
        labelId="timezone-label"
        inputProps={{ id: 'timezone' }}
        value={timezone}
        onChange={handleTimezoneChange}
        label="Timezone"
        onOpen={() => setSearchQuery('')}
        MenuProps={{
          PaperProps: {
            sx: { maxHeight: 450 },
          },
        }}
      >
        <Box
          sx={{
            p: 1,
            position: 'sticky',
            top: 0,
            bgcolor: 'background.paper',
            zIndex: 1,
          }}
        >
          <TextField
            size="small"
            placeholder="Search timezone..."
            fullWidth
            onChange={(e) => setSearchQuery(e.target.value)}
            onClick={(e) => e.stopPropagation()}
            value={searchQuery}
          />
        </Box>
        {filteredTimezones.map(({ value, label }) => (
          <MenuItem key={value} value={value}>
            <Box>
              <Typography variant="body2">{label}</Typography>
              <Typography variant="caption" color="text.secondary">
                {new Date().toLocaleTimeString(undefined, { timeZone: value })}
              </Typography>
            </Box>
          </MenuItem>
        ))}
      </Select>
      <Activity mode={errors.timezone ? 'visible' : 'hidden'}>
        <FormHelperText>{errors.timezone}</FormHelperText>
      </Activity>
    </FormControl>
  )

  return (
    <Card>
      <CardContent>
        <Typography variant="h6" component="h2" color="primary" gutterBottom>
          Settings
        </Typography>

        <Stack
          gap={3}
          direction={'row'}
          size={12}
          flexWrap={{ xs: 'wrap', md: 'nowrap' }}
          mt={2}
        >
          <Stack width="100%" gap={2}>
            <FormControl error={!!errors.dateFormat}>
              <InputLabel id="date-format-label" htmlFor="date-format">
                Date Format
              </InputLabel>
              <Select
                labelId="date-format-label"
                inputProps={{ id: 'date-format' }}
                value={dateFormat}
                onChange={handleDateFormatChange}
                label="Date Format"
              >
                {DATE_FORMAT_OPTIONS.map(({ value, label, example }) => (
                  <MenuItem key={value} value={value}>
                    <Box>
                      <Typography variant="body1">{label}</Typography>
                      <Typography variant="caption" color="text.secondary">
                        Example: {example}
                      </Typography>
                    </Box>
                  </MenuItem>
                ))}
              </Select>
              <Activity mode={errors.dateFormat ? 'visible' : 'hidden'}>
                <FormHelperText>{errors.dateFormat}</FormHelperText>
              </Activity>
            </FormControl>

            {renderTimezonePicker()}
          </Stack>
          <Stack width="100%" spacing={'auto'}>
            <FormControl>
              <FormLabel id="demo-theme-toggle">Theme</FormLabel>
              <RadioGroup
                aria-labelledby="demo-theme-toggle"
                name="theme-toggle"
                row
                value={mode}
                onChange={handleThemeSwitch}
              >
                <FormControlLabel
                  value="light"
                  control={<Radio />}
                  label="Light"
                />
                <FormControlLabel
                  value="dark"
                  control={<Radio />}
                  label="Dark"
                />
                <FormControlLabel
                  value="system"
                  control={<Radio />}
                  label="System"
                />
              </RadioGroup>
            </FormControl>
          </Stack>
        </Stack>
        <Button variant="contained" onClick={onSaveSettings} sx={{ mt: 2 }}>
          Save Settings
        </Button>
      </CardContent>
    </Card>
  )
}

export default UserInformationCard
